import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { RouterPaths } from '../../../app/appConstants';
import { useLangContext } from '../../../providers/LangProvider/LangProviderContext';

const LANDING_LANGUAGES = [
    { value: 'en', code: 'En', name: 'English' },
    { value: 'es', code: 'Es', name: 'Español' },
    { value: 'pt', code: 'Pt', name: 'Português' },
    { value: 'id', code: 'Id', name: 'Bahasa Indonesia' },
];

const LandingHeaderStyled = styled.header``;

function SectionLink({ hash, children }) {
    return (
        <Link to={{ pathname: RouterPaths.main, hash: `#${hash}` }}>
            {children}
        </Link>
    );
}

function LandingHeader() {
    const { getLangPath, locale, setLocale } = useLangContext();
    const [langOpen, setLangOpen] = useState(false);
    const langOpenRef = useRef(false);
    const pickerRef = useRef(null);
    const toggleRef = useRef(null);
    langOpenRef.current = langOpen;
    const current = LANDING_LANGUAGES.find((item) => item.value === locale) || LANDING_LANGUAGES[0];

    const closeLanguages = useCallback(() => {
        setLangOpen(false);
    }, []);

    useEffect(() => {
        const handlePointer = (event) => {
            if (pickerRef.current && !pickerRef.current.contains(event.target)) {
                setLangOpen(false);
            }
        };
        const handleKey = (event) => {
            if (event.key !== 'Escape' || !langOpenRef.current) return;
            setLangOpen(false);
            toggleRef.current?.focus();
        };
        document.addEventListener('click', handlePointer);
        document.addEventListener('keydown', handleKey);
        return () => {
            document.removeEventListener('click', handlePointer);
            document.removeEventListener('keydown', handleKey);
        };
    }, []);

    const selectLanguage = (value) => {
        setLocale(value);
        setLangOpen(false);
        toggleRef.current?.focus();
    };

    return (
        <LandingHeaderStyled className="wrap">
            <nav aria-label={getLangPath('landing.header.navLabel')}>
                <Link className="logo" to={{ pathname: RouterPaths.main, hash: '#audience' }}>
                    jetvix
                    <span>{getLangPath('landing.header.partners')}</span>
                </Link>
                <div className="nav-links">
                    <SectionLink hash="plans">{getLangPath('landing.header.plans')}</SectionLink>
                    <SectionLink hash="network">{getLangPath('landing.header.network')}</SectionLink>
                    <SectionLink hash="advantages">{getLangPath('landing.header.advantages')}</SectionLink>
                    <SectionLink hash="premium">{getLangPath('landing.header.premium')}</SectionLink>
                </div>
                <div className="header-actions">
                    <div className="language-picker" ref={pickerRef}>
                        <button
                            ref={toggleRef}
                            className="language-toggle"
                            id="language-toggle"
                            type="button"
                            aria-label={`${getLangPath('landing.header.language')}: ${current.name}`}
                            aria-expanded={langOpen}
                            aria-controls="language-options"
                            onClick={() => setLangOpen((open) => !open)}
                        >
                            <span id="language-code">{current.code}</span>
                            <span className="language-chevron" aria-hidden="true"/>
                        </button>
                        <div
                            id="language-options"
                            className="language-options"
                            role="group"
                            aria-label={getLangPath('landing.header.language')}
                            hidden={!langOpen}
                        >
                            {LANDING_LANGUAGES.map((item) => (
                                <button
                                    key={item.value}
                                    type="button"
                                    aria-pressed={item.value === current.value}
                                    onClick={() => selectLanguage(item.value)}
                                >
                                    {item.name}
                                </button>
                            ))}
                        </div>
                    </div>
                    <Link className="btn light login-button" id="partner-login" to={RouterPaths.login}>
                        {getLangPath('landing.header.login')}
                    </Link>
                </div>
            </nav>
        </LandingHeaderStyled>
    );
}

export default LandingHeader;
